import flow from "lodash/fp/flow";
import map from "lodash/fp/map";
import head from "lodash/fp/head";
import keys from "lodash/fp/keys";
import identity from "lodash/fp/identity";
import values from "lodash/fp/values";
import zipWith from "lodash/fp/zipWith";
import isNil from "lodash/fp/isNil";
import capitalize from "lodash/fp/capitalize";
import constants from "../../core/constants";
import { toObject, parsePrice, formatKey } from "../../core/utils";
import moment from "moment";

class CaptainPageTransform {
  constructor(Lists, TransactionsOps) {
    "ngInject";

    this.TransactionsOps = TransactionsOps;
    this.captainTransactionFields = [
      { title: "Date", name: "createdAt" },
      { title: "Total Amount", name: "totalAmount" },
      { title: "Operation", name: "op" },
      { title: "Method", name: "method" },
    ];
    this.captainLoanFields = [
      { name: "type", title: "Type" },
      { name: "total_amount", title: "Total amount" },
      { name: "remaining_amount", title: "Remaining amount" },
      { name: "installments_count", title: "Installments Count" },
      { name: "installments_interval", title: "Installments interval" },
      { name: "next_installments", title: "Upcoming Installment" },
      { name: "upcomingInstallments", title: "Upcoming amount" },
      { name: "createdAt", title: "Created At" },
      { name: "isFinalized", title: "Finalized" },
    ];
    this.captainBidsFields = [
      { name: "plan_name", title: "Plan Name" },
      { name: "bidding_id", title: "Bidding Campaign ID" },
      {
        name: "bidding_campaign.open_date",
        title: "Bidding Campaign start datetime",
        isSortable: true,
      },
      {
        name: "bidding_campaign.close_date",
        title: "Bidding Campaign end datetime",
        isSortable: true,
      },
      { name: "amount", title: "Bid Amount" },
      {
        name: "bidding_campaign.state",
        title: "Bidding Campaign State",
        isSortable: true,
      },
      { name: "updatedAt", title: "Bid Updated At", isSortable: true },
    ];
  }

  justify(data, isFlat) {
    const format = (item) => {
      item.is_tax_filer = { name: item.is_tax_filer };
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      item.rating = item.rating.toFixed(2);
      item.lastSentLocation = item.location
        ? moment(item.location.date).format(constants.dateTimeFormat)
        : "Unknown";
      item.lastAction = item.last_seen
        ? moment(item.last_seen).format(constants.dateTimeFormat)
        : "Unknown";
      item.extraInfo = item.extra_info
        ? this.justifyExtraInfo(item.extra_info)
        : null;
      item.national_id = item.national_id || null;
      item.email = item.email || "";
      item.walkme.tagsArray = item.walkme.tags.split(",");

      if (item.deactivation) {
        if (item.deactivation.deactivated_at) {
          item.deactivation.deactivatedAt = moment(
            item.deactivation.deactivated_at
          ).format(constants.dateTimeFormat);
        }
        if (item.deactivation.reasons) {
          item.deactivation.reasons = item.deactivation.reasons.map((reason) =>
            capitalize(reason).replace("_", " ")
          );
        }
      }

      if (item.garage.coordinates) {
        item.garage.coordinates = {
          value: item.garage.coordinates,
          isDisabled: true,
        };
      }

      return item;
    };
    return isFlat
      ? flow([format, identity])(data)
      : toObject(flow([format, identity])(data));
  }

  justifyNotes(data) {
    const format = (item) => {
      item = item._source
        ? Object.assign({}, item._source, { _id: item._id })
        : item;
      item.comment = { value: item.comment, isDisabled: true };
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);

      return item;
    };
    return map(format)(data);
  }

  justifyTransactions(data) {
    let method;
    const format = (item) => {
      item.totalAmount /= constants.currencyBy;
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      item.subTransactions.forEach((sub) => {
        if (sub.card) {
          method = "card";
        } else {
          method = "cash";
        }
      });
      item.method = { method };
      item.op = this.TransactionsOps.ops[item.operation];

      return item;
    };
    return map(format)(data);
  }

  justifyPlans(data) {
    return toObject(data);
  }

  justifyLoans(data) {
    const format = (item) => {
      item.type = capitalize(item.type);
      item.createdAt = moment(item.createdAt).format(constants.dateTimeFormat);
      item.upcomingInstallments =
        item.next_installments.length > 0
          ? head(item.next_installments).amount
          : null;
      item.next_installments =
        item.next_installments.length > 0
          ? moment(head(item.next_installments).date).format(
              constants.dateTimeFormat
            )
          : null;
      item.isFinalized = isNil(item.next_installments);
      item.total_amount = parsePrice(item.total_amount);
      item.remaining_amount = parsePrice(item.remaining_amount);
      item.upcomingInstallments = parsePrice(item.upcomingInstallments);

      return item;
    };

    return map(format)(data);
  }

  justifyExtraInfo(info) {
    const infoKeys = keys(info);
    const infoValues = values(info);

    return zipWith((key, value) => ({ key, value }), infoKeys, infoValues);
  }

  justifyVerificationState(data) {
    data.verification_state_per_type = data.verification_state_per_type.map(
      (verification) => {
        verification.type = formatKey(verification.type);
        verification.idfy_state = formatKey(verification.idfy_state);

        return verification;
      }
    );

    return data;
  }

  justifyBids(data) {
    const format = (item) => {
      item.plan_link = `${location.origin}/marketplace/plans/${item.bidding_campaign.plan_id}`;
      item.bidding_link = `${location.origin}/marketplace/plan-selling/${item.bidding_campaign.id}`;
      item.plan_name = item.bidding_campaign.plan_name;
      item.bidding_id = item.bidding_campaign.id;
      item["bidding_campaign.open_date"] = moment(
        item.bidding_campaign.open_date
      ).format(constants.dateTimeFormat);
      item["bidding_campaign.close_date"] = moment(
        item.bidding_campaign.close_date
      ).format(constants.dateTimeFormat);
      item.amount /= constants.currencyBy;
      item["bidding_campaign.state"] = item.bidding_campaign.state;
      item.updatedAt = moment(item.updated_at).format(constants.dateTimeFormat);

      return item;
    };

    return map(format)(data);
  }
}

export default CaptainPageTransform;



// WEBPACK FOOTER //
// ./src/pages/captain/captain.transforms.js